import { useId } from 'react';
import type { FormEventHandler, ReactNode } from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';

interface AppModalProps {
  isOpen: boolean;
  toggle: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Actions. On a phone the primary one spans the width, under the thumb. */
  footer?: ReactNode;
  /**
   * Makes the dialog a form around header, body and footer, so a submit
   * button in the footer submits it. The form is always `noValidate`: the
   * schema decides, and says so next to the field.
   */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  size?: 'sm' | 'lg' | 'xl';
  /** `'static'` for dialogs holding typed input a stray tap must not lose. */
  backdrop?: boolean | 'static';
  onClosed?: () => void;
  bodyClassName?: string;
}

/**
 * The dialog every form in the app opens in.
 *
 * Full screen on a phone (`fullscreen="sm"`, below 576px), a centred card
 * elsewhere. Header and footer stay put while the body scrolls, so the save
 * button is never a long scroll away from the field that was just filled.
 */
export default function AppModal({
  isOpen,
  toggle,
  title,
  children,
  footer,
  onSubmit,
  size,
  backdrop = true,
  onClosed,
  bodyClassName,
}: AppModalProps) {
  const titleId = useId();

  const content = (
    <>
      <div className="modal-header">
        <h2 className="modal-title" id={titleId}>
          {title}
        </h2>
        <button type="button" className="btn-close" aria-label="Cerrar" onClick={toggle} />
      </div>
      <ModalBody className={bodyClassName}>{children}</ModalBody>
      {footer && <ModalFooter>{footer}</ModalFooter>}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size={size}
      backdrop={backdrop}
      fullscreen="sm"
      scrollable
      centered
      modalClassName="v-modal"
      labelledBy={titleId}
      onClosed={onClosed}
    >
      {onSubmit ? (
        <form className="v-modal__form" onSubmit={onSubmit} noValidate>
          {content}
        </form>
      ) : (
        content
      )}
    </Modal>
  );
}
